import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScanSearch, Star, CircleCheck, Circle, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { Button } from "@/components/ui/button";

const FILTERS = [["all", "All"], ["not_reviewed", "Not reviewed"], ["reviewed", "Reviewed"]];

const PageRow = ({ p }) => (
  <Link to={`/pages/${p.id}`} data-testid={`page-row-${p.id}`} className="grid grid-cols-[auto_1fr_auto] md:grid-cols-[auto_1fr_auto_auto_auto] gap-4 items-center px-4 py-3.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors duration-150">
    {p.reviewed ? <CircleCheck className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-300" />}
    <div className="min-w-0">
      <div className="font-mono text-sm text-slate-900 truncate">{p.path}</div>
      <div className="text-xs text-slate-500 truncate">{p.title_en || "Untitled page"}</div>
    </div>
    <span className="hidden md:inline text-xs font-mono text-slate-500">{p.segments} sentences</span>
    <span className="hidden md:flex gap-1.5">
      {p.priority && <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200"><Star className="h-3 w-3" /> top-model check</span>}
      {p.pending > 0 && <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600"><Loader2 className="h-3 w-3 animate-spin" /> {p.pending} translating</span>}
      {p.needs_check > 0 && <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200"><AlertTriangle className="h-3 w-3" /> {p.needs_check} to check</span>}
    </span>
    <span className={`text-xs font-medium ${p.reviewed ? "text-emerald-700" : "text-slate-500"}`} data-testid={`page-status-${p.id}`}>{p.reviewed ? "Reviewed" : "Not reviewed"}</span>
  </Link>
);

export const PagesTab = ({ site, onChange }) => {
  const { session } = useSession();
  const [filter, setFilter] = useState("all");
  const [pages, setPages] = useState(null);

  const load = useCallback(() => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    return api.get(`/sites/${site.id}/pages${q}`).then((r) => setPages(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [site.id, filter]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const scan = async () => {
    try {
      await api.post(`/sites/${site.id}/scan`);
      toast.success("Scanning your website. Pages appear here as they're found.");
      setTimeout(() => { load(); onChange?.(); }, 6000);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex gap-1 p-1 bg-slate-100 rounded-md w-fit" data-testid="page-filter">
          {FILTERS.map(([v, l]) => (
            <button key={v} data-testid={`page-filter-${v}`} onClick={() => setFilter(v)} className={`px-3 py-1.5 text-xs rounded transition-colors duration-150 ${filter === v ? "bg-white shadow-sm text-slate-900 font-medium" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={scan} disabled={!session.can_edit} data-testid="scan-website-button"><ScanSearch className="h-4 w-4 mr-1.5" /> Scan website</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg" data-testid="pages-list">
        {pages === null ? (
          <div className="p-6 font-mono text-sm text-slate-500">Loading pages…</div>
        ) : pages.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="pages-empty">
            No pages yet. Install the {site.platform === "ghl" ? "tracking code" : "site kit"}, or press <strong>Scan website</strong>. Pages show up after the first visit.
          </div>
        ) : (
          pages.map((p) => <PageRow key={p.id} p={p} />)
        )}
      </div>
    </div>
  );
};
